import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import moment from "moment";
import { useBusinessAccountSelector } from "../../redux/selectors/useBusinessAccountSelectors";
import { Grid, GridContainer } from "../../Components/Grid";
import Icon from "../../Components/Icons";
import { ChartCard } from "../../Components/Charts";
import { formatteMinutesToReadableFormat } from "../../utiles/timeformate";
import { color, radius, shadow, fontFamily } from "../../Components/Theme";
import { REPETITION_PERIODS } from "../../constants";
import { SUCCESS } from "../../redux/constants/asyncStates";
import usePermissionsController from "../../permissions";
import useAnalyticsDispatchers from "../../redux/dispatchers/useAnalyticsDispatchers";
import {
  useEventAnalyticsSelector,
  useGetEventAnalyticsStatusSelector,
} from "../../redux/selectors/useAnalyticsSelectors";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: ${() => fontFamily()};
`;

const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 16px;
`;

const BusinessName = styled.h1`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: ${() => color("heading")};
`;

const TodayDate = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-variant-numeric: tabular-nums;
  color: ${() => color("text-muted")};
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media (min-width: 960px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 479px) {
    gap: 12px;
  }
`;

const KpiCard = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 20px 24px;
  background: ${() => color("surface")};
  border-radius: ${() => radius("card")}px;
  box-shadow: ${() => shadow("card")};
  @media (max-width: 479px) {
    padding: 16px;
  }
`;

const KpiText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const KpiLabel = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${() => color("text-body")};
`;

const KpiValue = styled.span`
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${() => color("heading")};
  white-space: nowrap;
  @media (max-width: 479px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const Badge = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${() => radius("control")}px;
  background: ${() => color("kpi-badge-bg")};
  @media (max-width: 479px) {
    display: none;
  }
`;

const Kpi = ({ label, value, icon }) => (
  <KpiCard>
    <KpiText>
      <KpiLabel>{label}</KpiLabel>
      <KpiValue>{value}</KpiValue>
    </KpiText>
    <Badge aria-hidden="true">
      <Icon $icon={icon} $size="22px" $color="kpi-badge-fg" />
    </Badge>
  </KpiCard>
);

const Home = () => {

  const businessAccount = useBusinessAccountSelector()
  const { accessAnalyticsPage } = usePermissionsController()
  const { getEventAnalytics } = useAnalyticsDispatchers()
  const eventAnalytics = useEventAnalyticsSelector()
  const eventAnalyticsStatus = useGetEventAnalyticsStatusSelector()

  const today = useMemo(() => new Date(), [])

  const totalNumCompletedEvents = useMemo(() => businessAccount.totalNumCompletedEvents, [businessAccount])
  const totalNumCancelledEvents = useMemo(() => businessAccount.totalNumCancelledEvents, [businessAccount])
  const totalNumStaff = useMemo(() => businessAccount.totalNumStaff, [businessAccount])
  const totalNumClients = useMemo(() => businessAccount.totalNumClients, [businessAccount])
  const totalNumMinutesCompleted = useMemo(() => businessAccount.totalNumMinutesCompleted, [businessAccount])
  const totalNumMissedEvents = useMemo(() => businessAccount.totalNumMissedEvents, [businessAccount])

  // Weekly charts reuse the Analytics endpoint (no new API).
  useEffect(() => {
    if (accessAnalyticsPage) {
      getEventAnalytics({ selectedDate: today, repetitionPeriods: REPETITION_PERIODS.WEEK })
    }
  }, [accessAnalyticsPage, today])

  const completedVisits = useMemo(
    () => eventAnalytics?.map((a) => ({ label: a.label, value: a.analytics.totalCompletedEvents })),
    [eventAnalytics]
  )
  const missedVisits = useMemo(
    () => eventAnalytics?.map((a) => ({ label: a.label, value: a.analytics.totalMissedEvents })),
    [eventAnalytics]
  )

  return (
    <Page>
      <Header>
        <BusinessName>{businessAccount?.businessName}</BusinessName>
        <TodayDate>{moment(today).format("dddd DD MMMM YYYY")}</TodayDate>
      </Header>

      <KpiGrid>
        <Kpi label="Completed Time" value={formatteMinutesToReadableFormat(totalNumMinutesCompleted ?? 0)} icon="AccessTime" />
        <Kpi label="Total Clients" value={totalNumClients ?? 0} icon="People" />
        <Kpi label="Total Staff" value={totalNumStaff ?? 0} icon="Badge" />
        <Kpi label="Completed Events" value={totalNumCompletedEvents ?? 0} icon="CheckCircleOutline" />
        <Kpi label="Cancelled Events" value={totalNumCancelledEvents ?? 0} icon="HighlightOff" />
        <Kpi label="Missed Events" value={totalNumMissedEvents ?? 0} icon="ErrorOutline" />
      </KpiGrid>

      {accessAnalyticsPage && eventAnalyticsStatus === SUCCESS && (
        <GridContainer $spacing={2}>
          <Grid $xs={12} $md={6}>
            <ChartCard
              title="Total completed events"
              data={completedVisits}
              view={REPETITION_PERIODS.WEEK}
              date={today}
              height={220}
            />
          </Grid>
          <Grid $xs={12} $md={6}>
            <ChartCard
              title="Total missed events"
              data={missedVisits}
              view={REPETITION_PERIODS.WEEK}
              date={today}
              height={220}
            />
          </Grid>
        </GridContainer>
      )}
    </Page>
  );
};

export default Home;
